import { PerformanceMonitor } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePersisted } from './persisted';
import {
  isQualitySetting,
  QUALITY_SETTINGS,
  type QualityProfile,
  type QualitySetting,
  qualityProfile,
} from './quality';

/** Auto watches the frame rate only after this long, so map building and shader compiles don't count. */
const AUTO_WARMUP_MS = 8000;
/** Auto drops to Low when the frame rate stays under this (whatever the display's rate). */
const AUTO_MIN_FPS = 40;

/**
 * The persisted Quality setting (High / Low / Auto) and what it draws now.
 * Auto's switch to Low lasts for this mount (the match): no flip-flopping.
 */
export function useQuality(): {
  setting: QualitySetting;
  setSetting: (setting: QualitySetting) => void;
  autoLow: boolean;
  onAutoDecline: () => void;
  profile: QualityProfile;
} {
  const [saved, setSetting] = usePersisted<string>('quality', 'auto');
  const setting: QualitySetting = isQualitySetting(saved) ? saved : 'auto';
  const [autoLow, setAutoLow] = useState(false);
  const onAutoDecline = useCallback(() => setAutoLow(true), []);
  const profile = useMemo(() => qualityProfile(setting, autoLow), [setting, autoLow]);
  return { setting, setSetting, autoLow, onAutoDecline, profile };
}

/**
 * Drives a `frameloop="never"` Canvas at up to `fps` frames per second.
 * The clock handed to `useFrame` starts one frame before the first one, so
 * nothing sees a huge first delta (on a fresh Canvas: changing `frameloop`
 * remounts it, as R3F zeroes the clock then).
 */
export function FrameLimiter({ fps }: { fps: number }) {
  const advance = useThree((s) => s.advance);
  useEffect(() => {
    const step = 1000 / fps;
    let frame = 0;
    let start: number | null = null;
    let last = Number.NEGATIVE_INFINITY;
    const loop = (now: number) => {
      frame = requestAnimationFrame(loop);
      start ??= now - step;
      // A little slack, so a 60 Hz display renders every other frame.
      if (now - last < step - 2) return;
      last = now;
      advance((now - start) / 1000);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [fps, advance]);
  return null;
}

/** Auto quality: after a warm-up, calls `onDecline` once the frame rate stays low. */
export function AutoQualityMonitor({ onDecline }: { onDecline: () => void }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setArmed(true), AUTO_WARMUP_MS);
    return () => clearTimeout(timer);
  }, []);
  if (!armed) return null;
  return <PerformanceMonitor bounds={autoBounds} onDecline={onDecline} />;
}

function autoBounds(): [number, number] {
  return [AUTO_MIN_FPS, Number.POSITIVE_INFINITY];
}

const LABELS: Record<QualitySetting, string> = { high: 'High', low: 'Low', auto: 'Auto' };
const TITLES: Record<QualitySetting, string> = {
  high: 'Full detail at up to 1.5× pixel ratio',
  low: 'Pixel ratio 1, no antialiasing, 30 fps, less fog and fewer trees: frees the CPU for hosting',
  auto: 'High, switching to Low for this match if the frame rate stays under 40 fps',
};

/** HUD pill: Quality High / Low / Auto (Auto says when it has switched to Low). */
export function QualityControl({
  setting,
  autoLow,
  onChange,
}: {
  setting: QualitySetting;
  autoLow: boolean;
  onChange: (setting: QualitySetting) => void;
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a pill of toggle buttons, not a form fieldset
    <div
      role="group"
      aria-label="Graphics quality"
      className="pill pointer-events-auto flex h-8 items-center gap-1 px-1.5 text-xs"
    >
      <span className="px-1 text-fg-subtle">Quality</span>
      {QUALITY_SETTINGS.map((s) => (
        <button
          key={s}
          type="button"
          aria-pressed={setting === s}
          title={s === 'auto' && autoLow ? `${TITLES.auto} (now Low)` : TITLES[s]}
          onClick={() => onChange(s)}
          className={`chip cursor-pointer ${setting === s ? 'chip-selected' : 'hover:bg-fill-hover'}`}
        >
          {LABELS[s]}
          {s === 'auto' && setting === 'auto' && autoLow ? ' · Low' : ''}
        </button>
      ))}
    </div>
  );
}
